// MeetingAllPanel.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

/* ---------------- helpers (tiny + fast) ---------------- */
const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const getId = (u) => (u && typeof u === "object" ? u._id : u) || "";
const getName = (u) =>
  (u && typeof u === "object" && (u.name || u.userId)) || "";
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString();
};
const fmtDateTime = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleString();
};
const normalizeTopics = (topics) =>
  asArray(topics)
    .map((t) => (typeof t === "object" ? t?.name || t?.title || t?._id : t))
    .filter(Boolean);

/* small, safe fetch */
async function safeFetchJSON(url, { signal } = {}) {
  const res = await fetch(url, { signal });
  let data = null;
  try {
    data = await res.json();
  } catch {}
  if (!res.ok)
    throw new Error(
      data?.message || data?.error || `Request failed (${res.status})`
    );
  return data;
}

/* ---------------- component ---------------- */
export default function MeetingAllPanel({
  apiBase,
  projectId,
  onBack,
  title = "All Meetings",
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // filters
  const [q, setQ] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [advOpen, setAdvOpen] = useState(false);
  const [adv, setAdv] = useState({
    from: "",
    to: "",
    minHours: "",
    maxHours: "",
    hasGeo: "", // '', 'yes', 'no'
    topic: "",
    attendee: "",
  });

  const abortRef = useRef(null);
  const navigate = useNavigate();

  const openEdit = useCallback(
    (it) => it?._id && navigate(`/edit/tbtmeeting/${it._id}`),
    [navigate]
  );

  /* ---------------- fetch ---------------- */
  useEffect(() => {
    const ac = new AbortController();
    abortRef.current = ac;

    (async () => {
      try {
        setLoading(true);
        setErr("");
        const url = new URL(apiBase + "/meeting");
        url.searchParams.set("project", projectId);
        const res = await safeFetchJSON(url.toString(), { signal: ac.signal });
        let list = Array.isArray(res) ? res : res?.data || [];

        // keep only this project (defensive)
        list = list.filter((it) => {
          const p = it?.project;
          const id = typeof p === "object" ? p?._id : p;
          return String(id || "") === String(projectId || "");
        });

        setRows(list);
      } catch (e) {
        if (e.name !== "AbortError")
          setErr(e.message || "Failed to load meetings.");
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [apiBase, projectId]);

  /* ---------------- options ---------------- */
  const ownerOptions = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      for (const u of [r?.TbtGivenBy, r?.createdby]) {
        const id = getId(u);
        if (id && !map.has(id)) map.set(id, getName(u) || "Unnamed");
      }
    }
    return [...map].map(([id, name]) => ({ id, name }));
  }, [rows]);

  /* tiny parser for date-only input to [start,end] of day */
  const parseBoundary = useCallback((s, end = false) => {
    if (!s) return null;
    const hasTime = /T|\d{1,2}:\d{2}/.test(s);
    const d = new Date(hasTime ? s : `${s}T${end ? "23:59:59" : "00:00:00"}`);
    return Number.isNaN(d.getTime()) ? null : d;
  }, []);

  /* ---------------- filtering + summary in one pass ---------------- */
  const { filtered, presenterSummary, quickStats } = useMemo(() => {
    const qi = q.trim().toLowerCase();
    const topicNeedle = adv.topic.trim().toLowerCase();
    const attendeeNeedle = adv.attendee.trim().toLowerCase();
    const fromT = parseBoundary(adv.from)?.getTime() ?? null;
    const toT = parseBoundary(adv.to, true)?.getTime() ?? null;
    const minH = adv.minHours ? Number(adv.minHours) : null;
    const maxH = adv.maxHours ? Number(adv.maxHours) : null;
    const wantGeo = adv.hasGeo; // '', 'yes', 'no'

    const out = [];
    const byOwner = new Map();

    let totalMeetings = 0;
    let totalAttendees = 0;
    const allTopics = new Set();

    for (const it of rows) {
      // quick haystack
      const topics = normalizeTopics(it?.typeOfTopic);
      const attendees = asArray(it?.attendees);
      const hay = [
        it?.reportId,
        it?.commentsBox,
        it?.geotagging,
        ...topics,
        ...attendees,
        getName(it?.TbtGivenBy),
        getName(it?.createdby),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      // search
      if (qi && !hay.includes(qi)) continue;

      // owner filter
      if (ownerId) {
        const ids = [getId(it?.TbtGivenBy), getId(it?.createdby)].filter(
          Boolean
        );
        if (!ids.map(String).includes(String(ownerId))) continue;
      }

      // date range (prefer `date`, fallback createdAt)
      const base = it?.date || it?.createdAt;
      const t = base ? new Date(base).getTime() : 0;
      if (fromT && (!t || t < fromT)) continue;
      if (toT && (!t || t > toT)) continue;

      // topic / attendee contains
      if (topicNeedle && !topics.join(" ").toLowerCase().includes(topicNeedle))
        continue;
      if (
        attendeeNeedle &&
        !attendees.join(" ").toLowerCase().includes(attendeeNeedle)
      )
        continue;

      // hours range
      const hrs = Number(it?.attendeesHours ?? NaN);
      if (minH !== null && !Number.isNaN(hrs) && hrs < minH) continue;
      if (maxH !== null && !Number.isNaN(hrs) && hrs > maxH) continue;

      // geotagging
      if (wantGeo === "yes" && !it?.geotagging) continue;
      if (wantGeo === "no" && it?.geotagging) continue;

      // ✅ passed -> keep
      out.push(it);

      // analytics
      totalMeetings += 1;
      const headcount = Number(it?.attendeesNos) || 0;
      totalAttendees += headcount;
      topics.forEach((tpc) => allTopics.add(tpc));

      // presenter summary
      const ownerLike = it?.TbtGivenBy || it?.createdby || null;
      const oid = getId(ownerLike) || "__unassigned__";
      const oname =
        oid === "__unassigned__"
          ? "Unassigned"
          : getName(ownerLike) || "Unnamed";
      let ent = byOwner.get(oid);
      if (!ent) {
        ent = {
          id: oid,
          name: oname,
          totalMeetings: 0,
          totalAttendees: 0,
          topics: new Set(),
          dates: new Set(),
          firstDate: null,
          lastDate: null,
        };
        byOwner.set(oid, ent);
      }
      ent.totalMeetings += 1;
ent.totalAttendees += headcount;
      topics.forEach((t) => t && ent.topics.add(t));
      if (it?.date) {
        const ds = fmtDate(it.date);
        ent.dates.add(ds);
        const dt = new Date(it.date);
        if (!ent.firstDate || dt < ent.firstDate) ent.firstDate = dt;
        if (!ent.lastDate || dt > ent.lastDate) ent.lastDate = dt;
      }
    }

    const presenterSummary = [...byOwner.values()].sort((a, b) => {
      if (a.id === "__unassigned__") return 1;
      if (b.id === "__unassigned__") return -1;
      return a.name.localeCompare(b.name);
    });

    const quickStats = {
      totalMeetings,
      totalPresenters: presenterSummary.length,
      uniqueTopics: allTopics.size,
      totalAttendees,
    };

    return { filtered: out, presenterSummary, quickStats };
  }, [rows, q, ownerId, adv, parseBoundary]);

  return (
    <div>
      {/* top bar */}
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h4 className="text-base font-semibold">
          {title}
          <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-blue-200">
            {filtered.length}
          </span>
        </h4>

        {/* quick analytics */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div className="rounded-xl bg-blue-50 p-3">
            <div className="text-gray-500 text-xs">Total Meetings</div>
            <div className="font-bold text-xl">{quickStats.totalMeetings}</div>
          </div>
          <div className="rounded-xl bg-green-50 p-3">
            <div className="text-gray-500 text-xs">Total TBT Presenters</div>
            <div className="font-bold text-xl">
              {quickStats.totalPresenters}
            </div>
          </div>
          <div className="rounded-xl bg-orange-50 p-3">
            <div className="text-gray-500 text-xs">Total Attendees</div>
            <div className="font-bold text-xl">{quickStats.totalAttendees}</div>
          </div>
        </div>

        {/* controls */}
        <div className="flex flex-wrap gap-2">
          <input
            className="w-56 rounded-xl border px-3 py-2 text-sm"
            placeholder="Search reportId, topic, attendee, comments..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            className="rounded-xl border px-2 py-2 text-sm"
            title="Filter by presenter / owner"
          >
            <option value="">All Owners</option>
            {ownerOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => setAdvOpen((v) => !v)}
            className="rounded-xl border px-3 py-2 text-sm"
          >
            {advOpen ? "Close Filters" : "Advanced Filters"}
          </button>
          {onBack && (
            <button
              onClick={onBack}
              className="rounded-xl border px-3 py-2 text-sm"
            >
              ← Back
            </button>
          )}
        </div>
      </div>

      {/* advanced filters */}
      {advOpen && (
        <div className="mb-3 grid gap-3 rounded-xl border bg-gray-50 p-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-xs text-gray-600">
            From
            <input
              type="date"
              className="mt-1 w-full rounded-md border px-2 py-1 text-sm bg-white"
              value={adv.from}
              onChange={(e) => setAdv((s) => ({ ...s, from: e.target.value }))}
            />
          </label>
          <label className="text-xs text-gray-600">
            To
            <input
              type="date"
              className="mt-1 w-full rounded-md border px-2 py-1 text-sm bg-white"
              value={adv.to}
              onChange={(e) => setAdv((s) => ({ ...s, to: e.target.value }))}
            />
          </label>
          <label className="text-xs text-gray-600">
            Topic contains
            <input
              className="mt-1 w-full rounded-md border px-2 py-1 text-sm bg-white"
              placeholder="e.g. Safety, Quality"
              value={adv.topic}
              onChange={(e) => setAdv((s) => ({ ...s, topic: e.target.value }))}
            />
          </label>
          <label className="text-xs text-gray-600">
            Attendee contains
            <input
              className="mt-1 w-full rounded-md border px-2 py-1 text-sm bg-white"
              placeholder="name / id"
              value={adv.attendee}
              onChange={(e) =>
                setAdv((s) => ({ ...s, attendee: e.target.value }))
              }
            />
          </label>
          <div className="flex items-end gap-2">
            <label className="flex-1 text-xs text-gray-600">
              Min Hours (attendeesHours)
              <input
                type="number"
                className="mt-1 w-full rounded-md border px-2 py-1 text-sm bg-white"
                value={adv.minHours}
                onChange={(e) =>
                  setAdv((s) => ({ ...s, minHours: e.target.value }))
                }
              />
            </label>
            <label className="flex-1 text-xs text-gray-600">
              Max Hours
              <input
                type="number"
                className="mt-1 w-full rounded-md border px-2 py-1 text-sm bg-white"
                value={adv.maxHours}
                onChange={(e) =>
                  setAdv((s) => ({ ...s, maxHours: e.target.value }))
                }
              />
            </label>
          </div>
          <label className="text-xs text-gray-600">
            Geotagging
            <select
              className="mt-1 w-full rounded-md border px-2 py-1 text-sm bg-white"
              value={adv.hasGeo}
              onChange={(e) =>
                setAdv((s) => ({ ...s, hasGeo: e.target.value }))
              }
            >
              <option value="">Any</option>
              <option value="yes">Present</option>
              <option value="no">Missing</option>
            </select>
          </label>
          <div className="col-span-full flex items-center gap-2">
            <button
              className="rounded-md border px-2 py-1 text-xs hover:bg-gray-100 bg-white"
              onClick={() =>
                setAdv({
                  from: "",
                  to: "",
                  minHours: "",
                  maxHours: "",
                  hasGeo: "",
                  topic: "",
                  attendee: "",
                })
              }
            >
              Reset
            </button>
            {(adv.from ||
              adv.to ||
              adv.minHours ||
              adv.maxHours ||
              adv.hasGeo ||
              adv.topic ||
              adv.attendee) && (
              <span className="text-xs text-gray-500">Filters active</span>
            )}
          </div>
        </div>
      )}

      {/* presenter summary table */}
      {loading ? (
        <div>Loading…</div>
      ) : err ? (
        <div className="text-red-600">{err}</div>
      ) : (
        <div className="overflow-hidden rounded-2xl border shadow-sm bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-100">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                <th className="px-4 py-3">TBT Given By</th>
                <th className="px-4 py-3">Total Meetings</th>
                <th className="px-4 py-3">Total Attendees</th>
                <th className="px-4 py-3">Dates Covered</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {presenterSummary.map((row) => (
                <tr
                  key={row.id}
                  className="hover:bg-indigo-50 cursor-default pointer-events-none"
                >
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {row.name}
                  </td>
                  <td className="px-4 py-3">{row.totalMeetings}</td>
                  <td className="px-4 py-3">{row.totalAttendees}</td>
                  <td className="px-4 py-3">
                    {[...row.dates].join(", ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
